import { useMemo } from "react";
import { Select } from "@/components/ui/select";
import {
  parseOverrideValue,
  settingsFooter,
  toggleRows,
  type SettingKey,
  type SettingOverride,
  type SettingsView,
} from "@/lib/publishToggles";

/** The per-package switches (type profile / LLVM PGO / text order, and
 *  "Tests first": run the package + full-project tests before the addon build
 *  and gate the publish on them — the default — or after the publish and the
 *  benchmark) with the global default next to each, the effective state with
 *  its reason ("off (operator)" / "off (default)") and the dependency note
 *  when one applies. Changing a switch applies to the next publish/rebuild;
 *  hover a switch's name for what it does. */
export function PublishToggles({
  name,
  settings,
  busyKey,
  onChange,
}: {
  name: string;
  settings: SettingsView | undefined;
  /** The key being saved (`<package>:<toggle>`), disables that select. */
  busyKey: string | null;
  onChange: (key: SettingKey, to: SettingOverride) => void;
}) {
  const rows = useMemo(
    () => (settings ? toggleRows(settings) : []),
    [settings],
  );
  const footer = useMemo(
    () => (settings ? settingsFooter(settings) : []),
    [settings],
  );
  if (!settings)
    return (
      <span className="text-xs text-muted-foreground">
        no settings reported
      </span>
    );
  return (
    <div className="space-y-1.5" data-testid={`toggles-${name}`}>
      {rows.map((r) => (
        <div key={r.key} className="text-xs" data-toggle={r.key}>
          <div className="flex items-center gap-2">
            <span className="w-20 shrink-0 font-medium" title={r.help}>
              {r.title}
            </span>
            <Select
              className="h-7 w-32 px-2 text-xs"
              aria-label={`${r.title} for ${name}`}
              title={r.help}
              value={r.override}
              disabled={busyKey === `${name}:${r.key}`}
              onChange={(e) => {
                const to = parseOverrideValue(e.target.value);
                if (to && to !== r.override) onChange(r.key, to);
              }}
            >
              <option value="inherit">{r.inheritLabel}</option>
              <option value="on">On</option>
              <option value="off">Off</option>
            </Select>
            <span
              className={
                r.effective === false
                  ? "text-muted-foreground"
                  : r.effective === true
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-muted-foreground"
              }
            >
              {r.effectiveLabel}
            </span>
          </div>
          {r.dependencyNote && (
            <div className="ml-[5.5rem] text-[11px] text-muted-foreground">
              {r.disabledReason ? `${r.disabledReason}. ` : ""}
              {r.dependencyNote}
            </div>
          )}
        </div>
      ))}
      <details className="text-[11px] text-muted-foreground">
        <summary className="cursor-pointer">How these combine</summary>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          {footer.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}
